// ============================================================
//  src/components/Footer.jsx
// ============================================================
//  No inline styles — all visual rules live in global.css.
//  Fonts: Cinzel (headings), Cormorant Garamond (body),
//         Cormorant SC (buttons/labels) — inherited from global.
// ============================================================

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import config from "../config";
import { supabase } from "../utils/supabase";

export default function Footer() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [subStatus, setSubStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  function go(path) {
    navigate(path);
    window.scrollTo(0, 0);
  }

  const handleSubscribe = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setSubStatus("error");
      return;
    }
    setLoading(true);
    setSubStatus(null);
    try {
      const { data: existing } = await supabase
        .from("newsletter_subscribers")
        .select("id")
        .eq("email", trimmed)
        .maybeSingle();

      if (existing) { setSubStatus("duplicate"); setLoading(false); return; }

      const { error } = await supabase
        .from("newsletter_subscribers")
        .insert([{ email: trimmed, subscribed_at: new Date().toISOString() }]);

      if (error) throw error;
      setSubStatus("success");
      setEmail("");
    } catch (err) {
      console.error("Newsletter error:", err);
      setSubStatus("error");
    } finally {
      setLoading(false);
    }
  };

  const statusConfig = {
    success:   { msg: "You're subscribed! Watch for our latest offers.", cls: "footer-status footer-status--success" },
    duplicate: { msg: "You're already subscribed. Thank you!",           cls: "footer-status footer-status--duplicate" },
    error:     { msg: "Please enter a valid email address.",             cls: "footer-status footer-status--error" },
  };

  return (
    <footer className="site-footer">
      <div className="footer-container">

        {/* ── Logo + description ── */}
        <div className="footer-col footer-brand">
          <div className="footer-logo" aria-hidden>
            <img
              src="/images/logo.png"
              alt={`${config.storeName} logo`}
              onError={(e) => { e.target.style.display = "none"; }}
            />
          </div>
          <p className="footer-desc">
            {config.storeName} is dedicated to providing wholesome, natural products crafted with care.
            We focus on quality, sustainability, and healthy living to help you embrace wellness every day.
          </p>
        </div>

        {/* ── Quick Links ── */}
        <div className="footer-col">
          <h4 className="footer-heading">Quick Links</h4>
          <ul className="footer-links-list">
            <li><button className="footer-link btn-press" onClick={() => go("/")}>Shop</button></li>
            <li><button className="footer-link btn-press" onClick={() => go("/subscription")}>Subscription</button></li>
            <li><button className="footer-link btn-press" onClick={() => go("/cart")}>Cart</button></li>
            <li><button className="footer-link btn-press" onClick={() => go("/orders")}>Orders</button></li>
            <li><button className="footer-link btn-press" onClick={() => go("/profile")}>Profile</button></li>
          </ul>
        </div>

        {/* ── Information ── */}
        <div className="footer-col">
          <h4 className="footer-heading">Information</h4>
          <ul className="footer-links-list">
            <li><button className="footer-link btn-press" onClick={() => go("/refund")}>Refund Policy</button></li>
          </ul>
        </div>

        {/* ── Newsletter + social ── */}
        <div className="footer-col footer-newsletter">
          <h4 className="footer-heading">Updates on Products &amp; Offers</h4>

          <div className="newsletter-row">
            <input
              className={`newsletter-input${loading ? " newsletter-input--disabled" : ""}`}
              placeholder="Enter your email"
              aria-label="Email address"
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setSubStatus(null); }}
              onKeyDown={(e) => e.key === "Enter" && handleSubscribe()}
              disabled={loading}
            />
            <button
              className={`subscribe-btn btn-press${loading ? " subscribe-btn--loading" : ""}`}
              onClick={handleSubscribe}
              disabled={loading}
              aria-busy={loading}
            >
              {loading ? "..." : "Subscribe"}
            </button>
          </div>

          {subStatus && (
            <p className={statusConfig[subStatus].cls}>
              {statusConfig[subStatus].msg}
            </p>
          )}

          <div className="footer-social">
            <a className="social-btn btn-press" href={config.social?.facebook  || "#"} target="_blank" rel="noreferrer" aria-label="Facebook">
              <i className="fa-brands fa-facebook-f" />
            </a>
            <a className="social-btn btn-press" href={config.social?.instagram || "#"} target="_blank" rel="noreferrer" aria-label="Instagram">
              <i className="fa-brands fa-instagram" />
            </a>
            <a className="social-btn btn-press" href={`https://wa.me/${config.whatsappNumber}`} target="_blank" rel="noreferrer" aria-label="WhatsApp">
              <i className="fa-brands fa-whatsapp" />
            </a>
          </div>
        </div>

      </div>

      <div className="footer-bottom">
        <p className="footer-copy">
          © {new Date().getFullYear()} {config.storeName}. All rights reserved.
        </p>
      </div>
    </footer>
  );
}
